import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import SkillIcon from '../general/SkillIcon';
import TableCell from './TableCell';
import moment from 'moment';
import './table.css';
import { operations as membershipOperations, selectors as membershipSelectors } from '../../state/modules/memberships';
import { operations as skillOperations, selectors as skillSelectors } from '../../state/modules/skills';
import { operations as userOperations, selectors as userSelectors } from '../../state/modules/users';
import {
  operations as certificationOperations,
  selectors as certificationSelectors
} from '../../state/modules/certifications';

import {
  operations as incidentFairnessPenaltyOperations,
  selectors as incidentFairnessPenaltySelectors
} from '../../state/modules/incident-fairness-penalty-overrides';

import Loader from '../general/Loader';
import { useWindowDimensions } from '../../utils/custom-hooks';
import Table from '../ui-kit/Table';

const emptyObject = {};
const emptyArray = [];

const IncidentFairnessEditTable = ({
  groupId,
  fetchSkills,
  fetchMemberships,
  fetchUsers,
  fetchCertifications,
  fetchOverrides,
  loaded,
  skills,
  memberships,
  usersById,
  membershipIds,
  certificationsByMembershipAndSkillId,
  overridesByMembershipAndSkillId,
  assignOverride
}) => {
  useEffect(() => {
    fetchSkills({ params: { group_ids: groupId } });
    fetchMemberships({
      params: { ids: membershipIds, presentation_type: 'basic' },
      forceFetch: true
    });
    // If an userId is provided as a prop, the component will be in
    // single user mode, and only need to fetch that user's data
    fetchUsers({ params: { group_ids: groupId, presentation_type: 'basic' } });
    fetchCertifications({ params: { group_ids: groupId } });
    fetchOverrides({ params: { group_ids: groupId } });
  }, []);

  const possessesSkill = useMemo(() => {
    const now = moment();
    const result = {};

    for (const membershipId of membershipIds) {
      const certificationsBySkillId = certificationsByMembershipAndSkillId[membershipId] || emptyObject;
      result[membershipId] = {};

      for (const skill of skills) {
        const certifications = certificationsBySkillId[skill.id] || emptyArray;
        result[membershipId][skill.id] = certifications.some(c =>
          c.end_time ? now.isBetween(c.start_time, c.end_time) : now.isAfter(c.start_time)
        );
      }
    }

    return result;
  }, [certificationsByMembershipAndSkillId, skills, membershipIds]);

  const { height } = useWindowDimensions();

  if (!loaded) return <Loader />;

  const membershipsToShow = memberships.filter(membership => membershipIds.includes(membership.id));
  return (
    <div className="overflow-y-scroll" style={{ maxHeight: height * 0.67 }}>
      <Table hover>
        <thead>
          <tr>
            <th />
            <th className="border text-center">{I18n.t('incidents.fairness_suggestion.selection_probability')}</th>
            <th colSpan={skills.length} className="border text-center">
              {I18n.t('activerecord.models.skill')}
            </th>
          </tr>
          <tr>
            <th className="position-sticky top-0 start-0 z-3">{I18n.t('activerecord.models.user')}</th>
            <th
              align="center"
              className="position-sticky top-0 z-2 text-center border-start border-end"
              style={{ minWidth: '75px' }}
            ></th>
            {skills.map(skill => (
              <th
                key={skill.id}
                align="center"
                className="position-sticky top-0 z-2 text-center"
                style={{ minWidth: '75px' }}
              >
                <SkillIcon skill={skill} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {membershipsToShow.map(membership => {
            // user may be empty because it is a monitor user
            const user = usersById[membership.user_id];
            if (!user) return null;
            return (
              <tr key={membership.id}>
                <td className="position-sticky start-0 z-1">
                  <a
                    href={`/groups/${membership.group_id}/reporting/incident_fairness/memberships/${membership.id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {user.full_name}
                  </a>
                </td>
                <td align="center" className="override-selector border-start border-end">
                  <TableCell
                    membershipId={membership.id}
                    skillId={null} // null skillId represents the general override for the membership
                    possessesSkill={true}
                    assignOverride={assignOverride}
                    overrideEntry={(overridesByMembershipAndSkillId[membership.id] || emptyObject)['null']}
                  />
                </td>
                {skills.map(skill => (
                  <td
                    align="center"
                    key={`membership_${membership.id}_skill_${skill.id}`}
                    className="override-selector"
                  >
                    <TableCell
                      membershipId={membership.id}
                      skillId={skill.id}
                      possessesSkill={possessesSkill[membership.id][skill.id]}
                      assignOverride={assignOverride}
                      overrideEntry={(overridesByMembershipAndSkillId[membership.id] || emptyObject)[skill.id]}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

const mapStateToProps = state => ({
  skills: skillSelectors.values(state),
  memberships: membershipSelectors.values(state),
  usersById: userSelectors.byId(state),
  loaded:
    userSelectors.initialLoadComplete(state) &&
    membershipSelectors.initialLoadComplete(state) &&
    skillSelectors.initialLoadComplete(state),
  certificationsByMembershipAndSkillId: certificationSelectors.certificationsByMembershipAndSkillId(state),
  overridesByMembershipAndSkillId: incidentFairnessPenaltySelectors.overridesByMembershipAndSkillId(state)
});

const mapDispatchToProps = {
  fetchSkills: skillOperations.fetch,
  fetchMemberships: membershipOperations.fetch,
  fetchUsers: userOperations.fetch,
  fetchCertifications: certificationOperations.fetch,
  fetchOverrides: incidentFairnessPenaltyOperations.fetch,
  assignOverride: incidentFairnessPenaltyOperations.assignOverride
};

export default connect(mapStateToProps, mapDispatchToProps)(IncidentFairnessEditTable);
